import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import { MdContentCopy } from '@react-icons/all-files/md/MdContentCopy';
import { RiShieldLine } from '@react-icons/all-files/ri/RiShieldLine';
import { IoMdInformationCircleOutline } from '@react-icons/all-files/io/IoMdInformationCircleOutline';
import moment from 'moment';
import {
    IssuedMcpToken,
    McpTokenScope,
    McpTokenSummary,
    getMcpEndpointUrl,
    issueMcpToken,
    listMcpTokens,
    revokeMcpToken,
} from 'Apis/library';
import { ToastsContext } from 'Rdx/contexts/ToastsContext';
import { ConfirmationModal } from 'Components/molecules';
import { ERROR_VARIANT, SUCCESS_VARIANT } from 'Core-utils/constants/constants';
import { SCOPE_OPTIONS } from './mcpScopes';
import { McpSkillsSection } from './McpSkillsSection';

const EXPIRY_OPTIONS = [30, 60, 90, 180, 365];

const GENERIC_ERROR = 'Something went wrong. Please try again.';

type ConfigFormat = 'cli' | 'json';

/** Adds or removes a scope, keeping the checkbox handler flat. */
const nextScopes = (current: McpTokenScope[], scope: McpTokenScope): McpTokenScope[] =>
    current.includes(scope) ? current.filter((existing) => existing !== scope) : [...current, scope];

/** Failed list load degrades to an empty table rather than an error state. */
const emptyTokenList = (): McpTokenSummary[] => [];

/**
 * Renders the connection snippet for a client. The token is interpolated here
 * and nowhere else, so it exists only for as long as the issued banner is shown.
 */
const buildConfigSnippet = (format: ConfigFormat, url: string, token: string): string => {
    if (format === 'cli') {
        return `claude mcp add --transport http amplifier ${url} \\\n  --header "Authorization: Bearer ${token}"`;
    }
    return JSON.stringify(
        { mcpServers: { amplifier: { type: 'http', url, headers: { Authorization: `Bearer ${token}` } } } },
        null,
        2,
    );
};

/**
 * Tenant admin management of customer-facing MCP credentials (APD-1818).
 *
 * These tokens let a customer point their own model at Amplifier's read-only
 * MCP surface. The plaintext is shown exactly once at creation and is not
 * recoverable, so the issued banner keeps it, and the connection snippet built
 * from it, on screen until dismissed.
 *
 * Every endpoint behind this section is admin-gated server-side; this component
 * relies on the enclosing tab for visibility, not for authorization.
 */
export const McpAccessSection = () => {
    const { addToast } = useContext(ToastsContext);
    const [loading, setLoading] = useState(true);
    const [tokens, setTokens] = useState<McpTokenSummary[]>([]);
    const [creating, setCreating] = useState(false);
    const [name, setName] = useState('');
    const [scopes, setScopes] = useState<McpTokenScope[]>(['amp.analytics.read', 'amp.insights.read']);
    const [expiresInDays, setExpiresInDays] = useState(90);
    const [issued, setIssued] = useState<IssuedMcpToken | null>(null);
    const [configFormat, setConfigFormat] = useState<ConfigFormat>('cli');
    const [pendingRevoke, setPendingRevoke] = useState<McpTokenSummary | null>(null);

    const endpointUrl = useMemo(() => getMcpEndpointUrl(), []);

    const refresh = useCallback(async () => {
        try {
            setTokens(await listMcpTokens());
        } catch {
            addToast({ severity: ERROR_VARIANT, body: 'Could not load MCP access tokens.' });
        } finally {
            setLoading(false);
        }
    }, [addToast]);

    useEffect(() => {
        let active = true;
        const load = async () => {
            const result = await listMcpTokens().catch(emptyTokenList);
            if (active) {
                setTokens(result);
                setLoading(false);
            }
        };
        void load();
        return () => {
            active = false;
        };
    }, []);

    const canCreate = useMemo(() => name.trim().length > 0 && scopes.length > 0, [name, scopes]);

    const configSnippet = useMemo(() => (issued ? buildConfigSnippet(configFormat, endpointUrl, issued.token) : ''), [
        issued,
        configFormat,
        endpointUrl,
    ]);

    const toggleScope = useCallback((scope: McpTokenScope) => {
        setScopes((current) => nextScopes(current, scope));
    }, []);

    const copyToClipboard = useCallback(
        async (value: string, label: string) => {
            try {
                await navigator.clipboard.writeText(value);
                addToast({ severity: SUCCESS_VARIANT, body: `${label} copied to your clipboard.` });
            } catch {
                addToast({ severity: ERROR_VARIANT, body: `Could not copy the ${label.toLowerCase()}.` });
            }
        },
        [addToast],
    );

    const handleCreate = useCallback(async () => {
        if (!canCreate) {
            return;
        }
        setCreating(true);
        try {
            const result = await issueMcpToken({ name: name.trim(), scopes, expiresInDays });
            setIssued(result);
            setName('');
            await refresh();
            addToast({ severity: SUCCESS_VARIANT, body: 'MCP access token created.' });
        } catch {
            addToast({ severity: ERROR_VARIANT, body: GENERIC_ERROR });
        } finally {
            setCreating(false);
        }
    }, [addToast, canCreate, expiresInDays, name, refresh, scopes]);

    const handleRevoke = useCallback(async () => {
        if (!pendingRevoke) {
            return;
        }
        try {
            await revokeMcpToken(pendingRevoke.id);
            await refresh();
            addToast({ severity: SUCCESS_VARIANT, body: `Revoked ${pendingRevoke.name}.` });
        } catch {
            addToast({ severity: ERROR_VARIANT, body: GENERIC_ERROR });
        } finally {
            setPendingRevoke(null);
        }
    }, [addToast, pendingRevoke, refresh]);

    const statusOf = useCallback((token: McpTokenSummary): 'Revoked' | 'Expired' | 'Active' => {
        if (token.revokedAt) {
            return 'Revoked';
        }
        if (moment(token.expiresAt).isBefore(moment())) {
            return 'Expired';
        }
        return 'Active';
    }, []);

    if (loading) {
        return (
            <div className="settings-v2__mcp-loading">
                <Spinner animation="border" size="sm" /> Loading MCP access tokens…
            </div>
        );
    }

    return (
        <div className="settings-v2__mcp-tab">
            <div className="settings-v2__section-header">
                <div className="settings-v2__section-icon">
                    <RiShieldLine size={24} />
                </div>
                <div>
                    <h2 className="settings-v2__section-title">MCP access</h2>
                    <p className="settings-v2__section-subtitle">
                        Connect your own model to Amplifier over MCP. These tokens are read-only and scoped to your
                        tenant.
                    </p>
                </div>
            </div>

            <div className="settings-v2__info-banner">
                <IoMdInformationCircleOutline size={18} />
                <span>
                    A token can only read data you already see in Amplifier. There is no write access, and every call is
                    rate limited and recorded in the audit trail.
                </span>
            </div>

            {issued && (
                <div className="settings-v2__card settings-v2__mcp-issued" role="alert">
                    <div className="settings-v2__card-header">
                        <h3 className="settings-v2__card-title">Copy your token now</h3>
                        <p className="settings-v2__card-subtitle">
                            This is the only time it will be shown. We store only a hash, so it cannot be recovered. If
                            you lose it, revoke this token and create another.
                        </p>
                    </div>

                    <div className="settings-v2__mcp-field">
                        <span className="settings-v2__mcp-field-label">Access token</span>
                        <input
                            className="settings-v2__mcp-field-input"
                            type="text"
                            readOnly
                            value={issued.token}
                            data-testid="mcp-issued-token"
                            aria-label="MCP access token"
                            onFocus={(event) => event.currentTarget.select()}
                        />
                        <button
                            type="button"
                            className="settings-v2__mcp-copy"
                            onClick={() => copyToClipboard(issued.token, 'Token')}
                            aria-label="Copy token"
                        >
                            <MdContentCopy size={16} />
                        </button>
                    </div>

                    <div className="settings-v2__mcp-config">
                        <div className="settings-v2__mcp-config-head">
                            <span className="settings-v2__setting-label">Add Amplifier to your client</span>
                            <div className="settings-v2__mcp-format-toggle" role="group" aria-label="Config format">
                                <button
                                    type="button"
                                    className={`settings-v2__mcp-format${
                                        configFormat === 'cli' ? ' settings-v2__mcp-format--active' : ''
                                    }`}
                                    onClick={() => setConfigFormat('cli')}
                                >
                                    Claude Code
                                </button>
                                <button
                                    type="button"
                                    className={`settings-v2__mcp-format${
                                        configFormat === 'json' ? ' settings-v2__mcp-format--active' : ''
                                    }`}
                                    onClick={() => setConfigFormat('json')}
                                >
                                    JSON config
                                </button>
                            </div>
                        </div>

                        <pre className="settings-v2__mcp-snippet" data-testid="mcp-config-snippet">
                            {configSnippet}
                        </pre>

                        <div className="settings-v2__mcp-config-actions">
                            <Button
                                className="settings-v2__mcp-btn"
                                onClick={() => copyToClipboard(configSnippet, 'Configuration')}
                            >
                                <MdContentCopy size={16} /> Copy configuration
                            </Button>
                            <Button variant="link" className="settings-v2__mcp-dismiss" onClick={() => setIssued(null)}>
                                I have saved it
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            <div className="settings-v2__cards-row settings-v2__mcp-row">
                <div className="settings-v2__card settings-v2__mcp-create">
                    <div className="settings-v2__card-header">
                        <h3 className="settings-v2__card-title">Create a token</h3>
                        <p className="settings-v2__card-subtitle">Grant only the access the integration needs.</p>
                    </div>

                    <div className="settings-v2__setting-row">
                        <div className="settings-v2__setting-label-group">
                            <label className="settings-v2__setting-label" htmlFor="mcp-token-name">
                                Name
                            </label>
                            <p className="settings-v2__setting-sublabel">Something you will recognise later.</p>
                        </div>
                        <div className="settings-v2__setting-control settings-v2__setting-control--wide">
                            <Form.Control
                                id="mcp-token-name"
                                className="settings-v2__mcp-input"
                                type="text"
                                value={name}
                                maxLength={128}
                                placeholder="e.g. Claude Desktop"
                                onChange={(event) => setName(event.target.value)}
                            />
                        </div>
                    </div>

                    <div className="settings-v2__mcp-scopes">
                        <span className="settings-v2__setting-label">Access</span>
                        {SCOPE_OPTIONS.map((option) => (
                            <label
                                className="settings-v2__mcp-scope"
                                key={option.value}
                                htmlFor={`mcp-scope-${option.value}`}
                            >
                                <input
                                    id={`mcp-scope-${option.value}`}
                                    type="checkbox"
                                    checked={scopes.includes(option.value)}
                                    onChange={() => toggleScope(option.value)}
                                />
                                <span className="settings-v2__mcp-scope-text">
                                    <span className="settings-v2__setting-label">{option.label}</span>
                                    <span className="settings-v2__setting-sublabel">{option.description}</span>
                                </span>
                            </label>
                        ))}
                    </div>

                    <div className="settings-v2__setting-row">
                        <div className="settings-v2__setting-label-group">
                            <label className="settings-v2__setting-label" htmlFor="mcp-token-expiry">
                                Expires in
                            </label>
                            <p className="settings-v2__setting-sublabel">Every token expires.</p>
                        </div>
                        <div className="settings-v2__setting-control">
                            <Form.Select
                                id="mcp-token-expiry"
                                className="settings-v2__mcp-input"
                                value={expiresInDays}
                                onChange={(event) => setExpiresInDays(Number(event.target.value))}
                            >
                                {EXPIRY_OPTIONS.map((days) => (
                                    <option key={days} value={days}>
                                        {days} days
                                    </option>
                                ))}
                            </Form.Select>
                        </div>
                    </div>

                    <div className="settings-v2__mcp-create-actions">
                        <Button
                            className="settings-v2__mcp-btn"
                            disabled={!canCreate || creating}
                            onClick={handleCreate}
                        >
                            {creating ? <Spinner animation="border" size="sm" /> : 'Create token'}
                        </Button>
                    </div>
                </div>

                <div className="settings-v2__card settings-v2__mcp-list">
                    <div className="settings-v2__card-header">
                        <h3 className="settings-v2__card-title">Active tokens</h3>
                        <p className="settings-v2__card-subtitle">
                            Revoking takes effect immediately, not at the next expiry.
                        </p>
                    </div>

                    {tokens.length === 0 ? (
                        <p className="settings-v2__mcp-empty">No MCP access tokens yet.</p>
                    ) : (
                        <div className="settings-v2__mcp-table-wrap">
                            <table className="settings-v2__mcp-table">
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Token</th>
                                        <th>Access</th>
                                        <th>Status</th>
                                        <th>Expires</th>
                                        <th>Last used</th>
                                        <th aria-label="Actions" />
                                    </tr>
                                </thead>
                                <tbody>
                                    {tokens.map((token) => {
                                        const status = statusOf(token);
                                        return (
                                            <tr key={token.id}>
                                                <td>{token.name}</td>
                                                <td>
                                                    <code>{token.prefix}…</code>
                                                </td>
                                                <td>
                                                    {token.scopes
                                                        .map((scope) => scope.replace('amp.', '').replace('.read', ''))
                                                        .join(', ')}
                                                </td>
                                                <td>
                                                    <span
                                                        className={`settings-v2__mcp-status settings-v2__mcp-status--${status.toLowerCase()}`}
                                                    >
                                                        {status}
                                                    </span>
                                                </td>
                                                <td>{moment(token.expiresAt).format('YYYY-MM-DD')}</td>
                                                <td>
                                                    {token.lastUsedAt ? moment(token.lastUsedAt).fromNow() : 'Never'}
                                                </td>
                                                <td className="settings-v2__mcp-actions-cell">
                                                    {!token.revokedAt && (
                                                        <Button
                                                            variant="link"
                                                            className="settings-v2__mcp-revoke"
                                                            onClick={() => setPendingRevoke(token)}
                                                        >
                                                            Revoke
                                                        </Button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            <McpSkillsSection onCopy={copyToClipboard} />

            <ConfirmationModal
                show={Boolean(pendingRevoke)}
                modalTitle="Revoke this token?"
                modalContent={`Anything using ${
                    pendingRevoke?.name ?? 'this token'
                } will stop working immediately. This cannot be undone.`}
                primaryButtonLabel="Revoke"
                buttonVariant="danger"
                onPrimaryButtonClick={handleRevoke}
                onClose={() => setPendingRevoke(null)}
            />
        </div>
    );
};

export default McpAccessSection;
